import React, { useMemo } from 'react'
import { timeUtils } from '../../services/timerService'
import { getTheme } from './themes'
import { Pause } from 'lucide-react'

interface TimerDisplayProps {
  elapsedTime: number
  isRunning: boolean
  className?: string
}

const TimerDisplay: React.FC<TimerDisplayProps> = ({ 
  elapsedTime, 
  isRunning, 
  className = '' 
}) => {
  const formattedTime = timeUtils.formatDuration(elapsedTime)
  
  // 從環境變數讀取主題並獲取主題配置
  const themeKey = import.meta.env.VITE_TIMER_THEME || 'normal'
  const theme = getTheme(themeKey)
  const ThemeWrapper = theme.Component

  // 分離時間數字，用於動畫效果
  const timeDigits = useMemo(() => {
    const parts = formattedTime.split(':')
    return {
      hours: parts[0] || '00',
      minutes: parts[1] || '00',
      seconds: parts[2] || '00'
    }
  }, [formattedTime])

  // 計時器內容
  const timerContent = (
    <div className={`relative ${className}`}>
      {/* 背景裝飾 */}
      <div className="absolute inset-0 overflow-hidden rounded-2xl md:rounded-3xl">
        {/* 漸層背景 */}
        <div className={`
          absolute inset-0 transition-all duration-700
          ${isRunning 
            ? 'bg-gradient-to-br from-green-50/80 via-white to-emerald-50/60' 
            : 'bg-gradient-to-br from-slate-50/80 via-white to-gray-50/60'
          }
        `} />
        
        {/* 光暈效果 */}
        {isRunning && (
          <>
            <div className="absolute -top-20 -right-20 w-40 h-40 md:w-60 md:h-60 rounded-full bg-green-200/30 blur-3xl animate-pulse" />
            <div className="absolute -bottom-10 -left-10 w-32 h-32 md:w-48 md:h-48 rounded-full bg-emerald-200/20 blur-2xl animate-pulse" style={{ animationDelay: '1s' }} />
          </>
        )}
      </div>

      {/* 主要內容 */}
      <div className="relative z-10 py-6 md:py-10 px-4 md:px-8">
        {/* 狀態標籤 */}
        <div className="flex justify-center mb-4 md:mb-6">
          <div className={`
            inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium
            transition-all duration-500
            ${isRunning 
              ? 'bg-green-100/80 text-green-700 shadow-sm shadow-green-200/50' 
              : 'bg-slate-100/80 text-slate-600'
            }
          `}>
            {isRunning ? (
              <>
                <div className="status-indicator status-indicator-running">
                  <div className="status-indicator-dot" />
                </div>
                <span>計時進行中</span>
              </>
            ) : (
              <>
                <Pause className="w-3.5 h-3.5" />
                <span>已暫停</span>
              </>
            )}
          </div>
        </div>

        {/* 時間顯示 */}
        <div className="flex items-center justify-center gap-1 md:gap-2">
          {/* 小時 */}
          <TimeDigitGroup 
            value={timeDigits.hours} 
            label="時" 
            isRunning={isRunning}
          />
          
          {/* 分隔符 */}
          <div className={`
            text-3xl md:text-5xl font-light mx-1 md:mx-2
            transition-all duration-300
            ${isRunning ? 'text-green-400 animate-pulse' : 'text-slate-300'}
          `}>
            :
          </div>
          
          {/* 分鐘 */}
          <TimeDigitGroup 
            value={timeDigits.minutes} 
            label="分" 
            isRunning={isRunning}
          />
          
          {/* 分隔符 */}
          <div className={`
            text-3xl md:text-5xl font-light mx-1 md:mx-2
            transition-all duration-300
            ${isRunning ? 'text-green-400 animate-pulse' : 'text-slate-300'}
          `} style={{ animationDelay: '0.5s' }}>
            :
          </div>
          
          {/* 秒鐘 */}
          <TimeDigitGroup 
            value={timeDigits.seconds} 
            label="秒" 
            isRunning={isRunning}
            isSeconds
          />
        </div>

        {/* 進度條動畫（僅運行時顯示） */}
        {isRunning && (
          <div className="mt-6 md:mt-8 flex justify-center">
            <div className="w-24 md:w-32 h-1 rounded-full bg-green-100 overflow-hidden">
              <div className="h-full w-full bg-gradient-to-r from-green-400 to-emerald-500 rounded-full animate-progress" />
            </div>
          </div>
        )}
      </div>
    </div>
  )

  // 使用主題包裝器包裝內容
  return (
    <ThemeWrapper isRunning={isRunning}>
      {timerContent}
    </ThemeWrapper>
  )
}

// 時間數字群組組件
interface TimeDigitGroupProps {
  value: string
  label: string
  isRunning: boolean
  isSeconds?: boolean
}

const TimeDigitGroup: React.FC<TimeDigitGroupProps> = ({ 
  value, 
  label, 
  isRunning,
  isSeconds = false 
}) => {
  return (
    <div className="flex flex-col items-center">
      <div className={`
        relative flex items-center justify-center
        min-w-[3.5rem] md:min-w-[5rem] h-14 md:h-20
        rounded-xl md:rounded-2xl
        transition-all duration-300
        ${isRunning 
          ? 'bg-white/80 shadow-lg shadow-green-100/50' 
          : 'bg-white/60 shadow-md shadow-slate-100/50'
        }
      `}>
        <span className={`
          text-3xl md:text-5xl font-bold font-mono tracking-tight
          transition-all duration-300
          ${isRunning 
            ? 'text-green-600' 
            : 'text-slate-700'
          }
          ${isSeconds && isRunning ? 'timer-display-running' : ''}
        `}
        style={{ fontVariantNumeric: 'tabular-nums' }}>
          {value}
        </span>
      </div>
      <span className={`
        mt-1.5 md:mt-2 text-xs font-medium uppercase tracking-wider
        transition-colors duration-300
        ${isRunning ? 'text-green-500' : 'text-slate-400'}
      `}>
        {label}
      </span>
    </div>
  )
}

export default TimerDisplay
